import { useEffect, useState } from 'react';
import { X, Calendar } from 'lucide-react';
import { useUpdateVotingSessionMutation } from '@/store/api/votingApi';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import UploadImage from '@/components/common/UploadImage';
interface Session {
  id: number;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  round: number;
  image: string | null;
}

interface EditSessionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  session: Session | null;
}

export default function EditSessionDialog({ open, setOpen, session }: EditSessionDialogProps) {
  const [updateVotingSession, { isLoading }] = useUpdateVotingSessionMutation();
  const [rounds, setRounds] = useState(1);
  const [imageLoading, setImageLoading] = useState(false);

  const [errors, setErrors] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    image: '',
  });
  const [form, setForm] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    image: '',
  });

  // convert api date → datetime-local

  const convertToDateTimeLocal = (dateString: string) => {
    return moment(dateString, 'D/M/YYYY, h:mm:ss a').format('YYYY-MM-DDTHH:mm');
  };

  // preload session data
  useEffect(() => {
    if (session) {
      setForm({
        title: session.title,
        description: session.description,
        startTime: convertToDateTimeLocal(session.startTime),
        endTime: convertToDateTimeLocal(session.endTime),
        image: session.image || '',
      });
      setRounds(session.round);
    }
  }, [session]);

  // reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setForm({
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        image: '',
      });
      setRounds(1);
    }
  }, [open]);

  const handleUpdateSession = async () => {
    let newErrors = {
      title: '',
      description: '',
      startTime: '',
      endTime: '',
      image: '',
    };

    if (!form.image) {
      newErrors.image = 'Banner image is required';
    }

    if (!form.title.trim()) {
      newErrors.title = 'Session title is required';
    }

    if (!form.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!form.startTime) {
      newErrors.startTime = 'Start date is required';
    }

    if (!form.endTime) {
      newErrors.endTime = 'End date is required';
    }

    setErrors(newErrors);

    if (
      newErrors.title ||
      newErrors.description ||
      newErrors.startTime ||
      newErrors.endTime ||
      newErrors.image
    ) {
      return;
    }
    try {
      await updateVotingSession({
        sessionId: session!.id,
        title: form.title,
        description: form.description,
        image: form.image,
        startTime: moment(form.startTime)
          .subtract(5, 'hours')
          .subtract(30, 'minutes')
          .format('YYYY-MM-DDTHH:mm'),
        endTime: moment(form.endTime)
          .subtract(5, 'hours')
          .subtract(30, 'minutes')
          .format('YYYY-MM-DDTHH:mm'),
        round: rounds,
      }).unwrap();
      toast.success('Voting session updated successfully');
      setOpen(false);
    } catch (err) {
      console.error('Failed to update voting session', err);
      toast.error('Failed to update voting session');
    }
  };
  if (!open || !session) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-[720px] bg-[#0b1220] border border-gray-800 rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 sm:p-8">
          {/* header */}
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-white">Edit Voting Session</h2>

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 hover:bg-gray-800 rounded"
            >
              <X className="text-gray-400 hover:text-white" />
            </button>
          </div>

          {/* form */}
          <div className="space-y-5">
            <div className="space-y-2">
              <UploadImage
                label="Banner"
                value={form.image}
                onChange={(url) => {
                  setForm((prev) => ({
                    ...prev,
                    image: url,
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    image: '',
                  }));
                }}
                setIsImageUploading={setImageLoading}
              />
              {errors.image && <p className="text-red-500 text-xs">{errors.image}</p>}
            </div>
            {/* title */}
            <div>
              <label className="text-sm text-gray-300">Session Title *</label>

              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="w-full mt-2 bg-[#050b18] border border-gray-700 rounded-lg px-4 py-2 text-white outline-none focus:border-blue-500"
              />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            </div>

            {/* description */}
            <div>
              <label className="text-sm text-gray-300">Description</label>

              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="w-full mt-2 bg-[#050b18] border border-gray-700 rounded-lg px-4 py-2 text-white outline-none focus:border-blue-500 resize-none"
              />
              {errors.description && <p className="text-red-500 text-xs">{errors.description}</p>}
            </div>

            {/* dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-300">Start Date</label>

                <div className="flex items-center gap-2 mt-2 border border-gray-700 bg-[#050b18] rounded-lg px-3 py-2">
                  <Calendar size={16} className="text-gray-500" />

                  <input
                    type="datetime-local"
                    value={form.startTime}
                    onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                    className="bg-transparent outline-none text-white w-full"
                  />
                </div>
                {errors.startTime && (
                  <p className="text-red-500 mt-1 text-xs">{errors.startTime}</p>
                )}
              </div>

              <div>
                <label className="text-sm text-gray-300">End Date</label>

                <div className="flex items-center gap-2 mt-2 border border-gray-700 bg-[#050b18] rounded-lg px-3 py-2">
                  <Calendar size={16} className="text-gray-500" />

                  <input
                    type="datetime-local"
                    value={form.endTime}
                    onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                    className="bg-transparent outline-none text-white w-full"
                  />
                </div>
                {errors.endTime && <p className="text-red-500 mt-1 text-xs">{errors.endTime}</p>}
              </div>

              {/* rounds */}
              <div>
                <label className="text-sm text-gray-300">Rounds *</label>

                <div className="flex items-center mt-2 border border-gray-700 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setRounds((r) => Math.max(1, r - 1))}
                    className="px-3 py-2 bg-[#050b18] hover:bg-[#0f172a]"
                  >
                    -
                  </button>

                  <input
                    readOnly
                    value={rounds}
                    className="w-full text-center bg-[#0b1220] text-white"
                  />

                  <button
                    type="button"
                    onClick={() => setRounds((r) => Math.min(10, r + 1))}
                    className="px-3 py-2 bg-[#050b18] hover:bg-[#0f172a]"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* footer */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-2 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleUpdateSession}
              className="px-5 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-white"
            >
              {isLoading ? 'Updating...' : 'Update Session'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
